// Tavoline — design tokens for the web app.
// Mirrors mobile/src/theme.ts 1:1 (same palette, same type personality).

const DEFAULT_ACCENT = "#B4472A"; // terracotta

export const colors = {
  paper: "#FAF6EF",
  paperDeep: "#F2ECE1",
  surface: "#FFFFFF",
  bg: "#FAF6EF",
  surfaceTertiary: "#F2ECE1",
  ink: "#221E18",
  inkSoft: "#7E7466",
  inkFaint: "#A79C8C",
  inkMuted: "#6B6255",
  rule: "#E7DFD2",
  line: "#E7DFD2",
  lineStrong: "#D8CDBB",
  accent: DEFAULT_ACCENT,
  accentSecondary: DEFAULT_ACCENT,
  onAccent: "#FFFFFF",
  accentSoft: "#F5E4DE",
  success: "#3F6B4A",
  successSoft: "#E6EFE8",
  danger: "#A33528",
  dangerSoft: "#F3E3DF",
  warning: "#9A6B1F",
};

export const spacing = {
  xs: 4,
  sm: 8,
  s12: 12,
  md: 16,
  lg: 22,
  xl: 32,
  xxl: 48,
  xxxl: 72,
};

export const radius = {
  photo: 3,
  sm: 3,
  md: 4,
  lg: 4,
  sheet: 18,
  pill: 999,
};

const SERIF = '"Instrument Serif", Georgia, serif';
const SANS = '"Manrope", system-ui, sans-serif';

// Font helpers return style fragments (fontFamily + weight/style).
export const fonts = {
  serif: SERIF,
  sans: SANS,
};
export const type = {
  serif: { fontFamily: SERIF, fontWeight: 400 },
  serifBold: { fontFamily: SERIF, fontWeight: 400 },
  serifItalic: { fontFamily: SERIF, fontWeight: 400, fontStyle: "italic" },
  sans: { fontFamily: SANS, fontWeight: 400 },
  sansMedium: { fontFamily: SANS, fontWeight: 500 },
  sansSemi: { fontFamily: SANS, fontWeight: 600 },
  sansBold: { fontFamily: SANS, fontWeight: 700 },
};

// ── Tenant accent: ensure contrast ≥ 4.5:1 against white ──
function hexToRgb(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function luminance([r, g, b]) {
  const a = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
}
function contrastWithWhite(rgb) {
  return (1.0 + 0.05) / (luminance(rgb) + 0.05);
}
function rgbToHex([r, g, b]) {
  return "#" + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("");
}
export function ensureAccent(hex) {
  if (!hex || !/^#?[0-9a-fA-F]{3,6}$/.test(hex)) return DEFAULT_ACCENT;
  let rgb = hexToRgb(hex);
  let guard = 0;
  while (contrastWithWhite(rgb) < 4.5 && guard < 24) {
    rgb = [rgb[0] * 0.9, rgb[1] * 0.9, rgb[2] * 0.9];
    guard++;
  }
  return rgbToHex(rgb);
}

export const TAG_LABELS = {
  veg: "VEG",
  vegan: "VEGAN",
  piccante: "PICCANTE",
  senza_glutine: "SENZA GLUTINE",
  surgelato: "SURGELATO",
};

export const ALLERGEN_LABELS = {
  glutine: "Glutine",
  crostacei: "Crostacei",
  uova: "Uova",
  pesce: "Pesce",
  arachidi: "Arachidi",
  soia: "Soia",
  latte: "Latte",
  frutta_a_guscio: "Frutta a guscio",
  sedano: "Sedano",
  senape: "Senape",
  sesamo: "Sesamo",
  solfiti: "Solfiti",
  lupini: "Lupini",
  molluschi: "Molluschi",
};

export function formatCents(cents) {
  const euros = ((cents || 0) / 100).toFixed(2).replace(".", ",");
  return `${euros} €`;
}

export function initials(name) {
  const parts = (name || "").trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
